/**
 * End of run, both ways.
 *
 * Defeat and victory share one modal and swap three things: the mark, the
 * border colour, and the verb on the primary button. The score sheet is the
 * same either way, because the numbers are what the player wants to read back.
 */

import { el, groupDigits, icon, money } from './Icon';
import type { IconName } from './Icon';
import { ModalShell } from './PauseMenu';
import { logoNode } from './Logo';
import { HighScores, scoreRun } from './HighScores';
import type { ScoreEntry } from './HighScores';
import { InitialsEntry } from './InitialsEntry';

export interface GameOverState {
  open: boolean;
  victory: boolean;
  /** Round the run ended on. */
  round: number;
  totalRounds: number;
  kills: number;
  cashEarned: number;
  livesLost: number;
  towersBuilt: number;
  /** Lives still held when the run ended. Zero on a defeat, by definition. */
  livesRemaining: number;
  /** Sim seconds elapsed. */
  duration: number;
  /** The run's seed, so a good board can be replayed. */
  seed?: string;
}

export interface GameOverCallbacks {
  onRetry?(): void;
  /** Copies the seed. The panel does the clipboard write itself. */
  onCopySeed?(seed: string): void;
}

export class GameOver {
  private shell = new ModalShell('gameover-title');
  private summary = el('p', 'toast__text');
  private values = new Map<string, HTMLElement>();
  private retryBtn = el('button', 'btn btn--primary');
  private retryLabel = el('span', undefined, 'Try again');
  private seedBtn = el('button', 'btn');
  private seed = '';
  private wasVictory: boolean | null = null;
  private mounted = false;

  // ── the board ──
  private readonly scores: HighScores;
  private hiscore = el('section', 'hiscore');
  private hiscoreTotal = el('span', 'hiscore__total num', '0');
  private entryBox = el('div', 'hiscore__entry');
  private entryLabel = el('p', 'hiscore__rank');
  private reels = new InitialsEntry({
    onSubmit: () => this.fileScore(),
    onChange: () => { this.fileBtn.disabled = false; },
  });
  private fileBtn = el('button', 'btn btn--primary hiscore__file');
  private table = el('ol', 'hiscore__table');
  private missNote = el('p', 'hiscore__miss');
  /**
   * Which run the block on screen belongs to.
   *
   * `update` is called again whenever the sheet opens or shuts, and the state
   * arrives as a fresh object every time, so identity says nothing. Rebuilding
   * on every call would wipe three letters the player was halfway through
   * choosing the moment anything else repainted.
   */
  private paintedRun = '';
  private filed = false;
  private lastRun = { round: 0, kills: 0, livesRemaining: 0 };

  constructor(private readonly cb: GameOverCallbacks = {}, scores = new HighScores()) {
    this.scores = scores;
  }

  mount(root: HTMLElement): void {
    if (this.mounted) return;
    this.mounted = true;
    this.shell.mount(root);
    this.shell.setTitle('Run over', 'octagon-times', 'uiDanger');
    // A finished run is not dismissable by accident: only the buttons leave.
    this.shell.onDismiss = null;

    const score = el('dl', 'score');
    score.append(
      this.row('round', 'flag-checkered', 'Rounds cleared'),
      this.row('kills', 'hockey-mask', 'Kills'),
      this.row('cash', 'coins', 'Cash earned'),
      this.row('lives', 'heart', 'Lives lost'),
      this.row('towers', 'shapes', 'Towers built'),
      this.row('time', 'clock', 'Time'),
    );

    this.retryBtn.type = 'button';
    this.retryBtn.append(icon('refresh', { size: 16 }), this.retryLabel);
    this.retryBtn.addEventListener('click', this.onRetry);

    /*
     * No MAIN MENU button. There is no main menu — the cabinet boots straight
     * into a run — and the button ran `session.start()`, which is what TRY
     * AGAIN does. Two controls, one behaviour, and one of them naming a screen
     * that has never existed. When there is somewhere to go back to, this is
     * where the way back goes.
     */

    this.seedBtn.type = 'button';
    this.seedBtn.append(icon('copy', { size: 16 }), document.createTextNode('Copy seed'));
    this.seedBtn.addEventListener('click', this.onCopySeed);
    this.seedBtn.hidden = true;

    const actions = el('div', 'modal__actions');
    actions.append(this.retryBtn, this.seedBtn);

    this.shell.box.append(logoNode('sm'), this.summary, score, this.buildHiscore(), actions);
  }

  /**
   * The high-score block: the run's number, the entry reels, and the table.
   *
   * Built once and shown or hidden per run, rather than rebuilt — the reels
   * hold DOM focus while the player is using them and a rebuild drops it.
   */
  private buildHiscore(): HTMLElement {
    const head = el('div', 'hiscore__head');
    const label = el('span', 'hiscore__label', 'Score');
    head.append(label, this.hiscoreTotal);

    this.fileBtn.type = 'button';
    this.fileBtn.append(icon('trophy', { size: 16 }), document.createTextNode('Enter'));
    this.fileBtn.addEventListener('click', this.onFile);

    this.entryBox.append(this.entryLabel, this.reels.root, this.fileBtn);
    this.entryBox.hidden = true;
    this.missNote.hidden = true;

    this.hiscore.append(head, this.entryBox, this.missNote, this.table);
    return this.hiscore;
  }

  update(state: GameOverState): void {
    if (!this.mounted) return;

    if (state.victory !== this.wasVictory) {
      this.wasVictory = state.victory;
      this.shell.box.classList.toggle('modal--victory', state.victory);
      this.shell.box.classList.toggle('modal--defeat', !state.victory);
      // `trophy` for a cleared board, `octagon-times` for a leaked one. Both are
      // vendored marks; neither is a skull, because the set has no skull.
      this.shell.setTitle(
        state.victory ? 'Board cleared' : 'Run over',
        state.victory ? 'trophy' : 'octagon-times',
        state.victory ? 'uiCash' : 'uiDanger',
      );
      this.retryLabel.textContent = state.victory ? 'Play again' : 'Try again';
    }

    this.summary.textContent = state.victory
      ? `All ${state.totalRounds} rounds held.`
      : `The line broke on round ${state.round}.`;

    this.set('round', `${state.round}/${state.totalRounds}`);
    this.set('kills', groupDigits(state.kills));
    this.set('cash', money(state.cashEarned));
    this.set('lives', groupDigits(state.livesLost));
    this.set('towers', groupDigits(state.towersBuilt));
    this.set('time', clock(state.duration));

    this.seed = state.seed ?? '';
    this.seedBtn.hidden = this.seed === '';

    this.paintHiscore(state);

    const wasOpen = this.shell.open;
    this.shell.setOpen(state.open);
    // Focus lands on the reels rather than on TRY AGAIN when there is a score
    // to file. `ModalShell.setOpen` focuses the first control in the box, and
    // the first thing a player who just made the board wants is the letters.
    if (state.open && !wasOpen && !this.entryBox.hidden) this.reels.focus();
  }

  destroy(): void {
    this.retryBtn.removeEventListener('click', this.onRetry);
    this.seedBtn.removeEventListener('click', this.onCopySeed);
    this.fileBtn.removeEventListener('click', this.onFile);
    this.reels.destroy();
    this.shell.destroy();
    this.values.clear();
    this.mounted = false;
  }

  // ── high score ──

  /** The run's outcome, in the three terms the score is made of. */
  private outcome(state: GameOverState): {
    round: number; kills: number; livesRemaining: number;
  } {
    return {
      round: state.round,
      kills: state.kills,
      livesRemaining: state.livesRemaining,
    };
  }

  private paintHiscore(state: GameOverState): void {
    /*
     * A run is identified by what it did, not by the object carrying it. Seed
     * is in the key because two runs can end identically on the same board and
     * still be two runs; round and kills are in it because a seedless build
     * still has to tell them apart.
     */
    const key = `${state.seed ?? ''}|${state.round}|${state.kills}|${state.duration}`;
    const run = this.outcome(state);
    this.lastRun = run;
    const total = scoreRun(run);
    this.hiscoreTotal.textContent = groupDigits(total);

    if (key === this.paintedRun) return;
    this.paintedRun = key;
    this.filed = false;

    const rank = this.scores.qualifies(total)
      ? this.scores.list().filter((e) => e.score >= total).length
      : -1;

    if (rank >= 0) {
      this.entryBox.hidden = false;
      this.missNote.hidden = true;
      this.fileBtn.disabled = false;
      this.entryLabel.textContent = `Rank ${rank + 1} — enter your initials`;
      // Whoever played last is who is most likely playing now.
      this.reels.value = this.scores.list()
        .slice()
        .sort((a, b) => b.at - a.at)[0]?.initials ?? 'AAA';
      /*
       * The board waits until the letters are in.
       *
       * Ten rows is 260px and the sheet already carries a title, the mark, six
       * score rows and two buttons; with the reels on screen as well it ran
       * 150px past the modal's 84% ceiling on a 390×844, and what fell off the
       * bottom was TRY AGAIN. Holding the table back is not a space trick — the
       * table's whole point is the highlighted row, and there is nothing to
       * highlight until the player has filed. One thing at a time, which is how
       * the cabinet this is imitating did it too.
       */
      this.table.hidden = true;
    } else {
      this.entryBox.hidden = true;
      const board = this.scores.list();
      const cut = board[board.length - 1]?.score ?? 0;
      this.missNote.hidden = false;
      this.missNote.textContent = `${groupDigits(cut - total)} short of the board.`;
      this.table.hidden = false;
      this.renderTable(-1);
    }
  }

  /** Files the run under the reels' letters, then swaps entry for the table. */
  private fileScore(): void {
    if (this.filed) return;
    this.filed = true;
    this.fileBtn.disabled = true;
    const at = this.scores.submit(this.lastRun, this.reels.value);
    this.entryBox.hidden = true;
    this.missNote.hidden = true;
    this.table.hidden = false;
    this.renderTable(at);
    // The sheet's focus trap has one fewer control in it now, and the one the
    // player wants next is the one that starts the next run.
    this.retryBtn.focus();
  }

  /**
   * The board. `mine` is the row to light up, or `-1` for none.
   *
   * Ten rows of four `textContent` writes, rebuilt only when the board changes
   * — which is at most once per run.
   */
  private renderTable(mine: number): void {
    this.table.textContent = '';
    const board = this.scores.list();
    if (board.length === 0) {
      this.table.append(el('li', 'hiscore__empty', 'No scores yet. This one is first.'));
      return;
    }
    board.forEach((e: ScoreEntry, i) => {
      const row = el('li', 'hiscore__row' + (i === mine ? ' is-mine' : ''));
      row.append(
        el('span', 'hiscore__pos num', String(i + 1).padStart(2, '0')),
        el('span', 'hiscore__who', e.initials),
        el('span', 'hiscore__round num', `R${e.round}`),
        el('span', 'hiscore__pts num', groupDigits(e.score)),
      );
      this.table.append(row);
    });
  }

  private row(key: string, name: IconName, label: string): DocumentFragment {
    const frag = document.createDocumentFragment();
    const dt = el('dt');
    dt.append(icon(name, { size: 12 }), document.createTextNode(label));
    const dd = el('dd', 'num', '—');
    this.values.set(key, dd);
    frag.append(dt, dd);
    return frag;
  }

  private set(key: string, text: string): void {
    const node = this.values.get(key);
    if (node) node.textContent = text;
  }

  private readonly onRetry = (): void => { this.cb.onRetry?.(); };

  private readonly onFile = (): void => { this.fileScore(); };

  private readonly onCopySeed = (): void => {
    if (!this.seed) return;
    void navigator.clipboard?.writeText(this.seed).catch(() => undefined);
    this.cb.onCopySeed?.(this.seed);
  };
}

/** `12:04` from seconds. Minutes never pad, seconds always do. */
function clock(seconds: number): string {
  const total = Math.max(0, Math.floor(seconds));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}
